"use client";

import { useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { BarChart3, Flame, History, Loader2, Target, BookOpen, Code } from "lucide-react";
import {
  MyAside,
  MyAsideStat,
  MyAsideCta,
} from "@/components/user-button-pages/MyAside";
import { Button } from "@/components/ui/button";
import PreviousSessionDialog from "@/components/PreviousSessionDialog";
import { getPassInfoFromMetadata } from "@/components/user-button-pages/helpers";
import { hasFeatureAccess } from "@/lib/pass-utils";
import { PASS_FEATURES } from "@/types/pass";
import {
  getAttendanceStreak,
  type AttendanceStreakResult,
} from "@/lib/server-actions/attendanceRecordActions";
import type { StudyProgressTab } from "@/components/user-button-pages/StudyProgressPage";
import { cn } from "@/lib/utils";

const TABS: Array<{ key: StudyProgressTab; label: string; icon: typeof Target }> =
  [
    { key: "overview", label: "전체", icon: Target },
    { key: "theory", label: "이론", icon: BookOpen },
    { key: "coding", label: "코딩", icon: Code },
    { key: "problems", label: "문제", icon: BarChart3 },
  ];

export function StudyProgressTabControl({
  activeTab,
  setActiveTab,
  compact = false,
}: {
  activeTab: StudyProgressTab;
  setActiveTab: (t: StudyProgressTab) => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div
        className="grid grid-cols-4 gap-1 border-b border-border"
        role="tablist"
        aria-label="공부 진행상황 탭"
      >
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTab(key)}
              className={cn(
                "inline-flex min-h-10 min-w-0 items-center justify-center gap-1 rounded-b-none border-b-2 px-1.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                active
                  ? "border-amber-500 bg-amber-50 text-amber-700 hover:bg-amber-50 hover:text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 dark:hover:bg-amber-950/50"
                  : "border-transparent text-foreground/70 hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-4 gap-1.5"
      role="tablist"
      aria-label="공부 진행상황 탭"
    >
      {TABS.map(({ key, label, icon: Icon }) => {
        const active = activeTab === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setActiveTab(key)}
            className={cn(
              "flex items-center rounded-lg border text-[12px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              "min-h-12 flex-col gap-1 px-1 py-2",
              active
                ? "border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-300"
                : "border-border bg-background text-foreground/70 hover:text-foreground",
            )}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function StudyProgressTabCard({
  activeTab,
  setActiveTab,
  className,
}: {
  activeTab: StudyProgressTab;
  setActiveTab: (t: StudyProgressTab) => void;
  className?: string;
}) {
  return (
    <MyAside icon={BarChart3} title="진행상황 보기" className={className}>
      <StudyProgressTabControl
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </MyAside>
  );
}

export function StudyProgressStickyControls({
  activeTab,
  setActiveTab,
}: {
  activeTab: StudyProgressTab;
  setActiveTab: (t: StudyProgressTab) => void;
}) {
  return (
    <>
      <div className="fixed inset-x-0 top-12 z-40 border-b border-border bg-background/95 py-2 shadow-sm backdrop-blur sm:top-16 lg:hidden">
        <div className="mx-auto w-full max-w-4xl px-4">
          <StudyProgressTabControl
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            compact
          />
        </div>
      </div>
      <div aria-hidden className="h-14 lg:hidden" />
    </>
  );
}

/**
 * 공부 진행상황 탭의 우측 맥락 카드 (JCG-2073 → JCG-2126).
 *
 * - 탭 전환(전체/이론/코딩/문제)을 우측 사이드바에서 하도록 이전(라우트가 탭 상태 소유, 제어형).
 * - 연속학습일(streak) 위젯: JCG-2070 의 getAttendanceStreak server action 을 **재사용**(표시만,
 *   집계 재구현 금지). 현재 연속일 + 활성 회차 출석 일수 + 최근 7일 링.
 * - 이전 회차 보기(PreviousSessionDialog): 셸(데스크탑)에서 본문 인라인 대신 우측 사이드바에서
 *   회차를 선택하도록 통일(JCG-2126). 탭별로 stats(overview/problems)·attendance(theory/coding) 모드.
 * - 잠금 안내/바로가기는 기존(JCG-2073) 유지.
 */
export default function StudyProgressAside({
  activeTab,
  setActiveTab,
  hideTabControl = false,
}: {
  activeTab: StudyProgressTab;
  setActiveTab: (t: StudyProgressTab) => void;
  hideTabControl?: boolean;
}) {
  const { user } = useUser();
  const passInfo = getPassInfoFromMetadata(user?.publicMetadata);
  const starterPurchasedAt =
    (user?.publicMetadata?.starterPurchasedAt as string | null) ?? null;
  const hasAccess = hasFeatureAccess(
    passInfo.passType,
    PASS_FEATURES.studyProgress,
    starterPurchasedAt,
  );

  // 연속학습일 — JCG-2070 server action 재사용(집계 비침투, 표시만).
  // hasAccess 가 아니면 fetch 를 건너뛰되 로딩은 finally 에서 일괄 해제(early setState-in-effect 회피).
  const [streak, setStreak] = useState<AttendanceStreakResult | null>(null);
  const [streakLoading, setStreakLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!hasAccess) return;
        const res = await getAttendanceStreak();
        if (alive) setStreak(res);
      } catch (e) {
        console.error("연속학습일 로드 오류:", e);
      } finally {
        if (alive) setStreakLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [hasAccess]);

  return (
    <div className="space-y-3">
      {/* 탭 전환 — 우측 사이드바에서 전체/이론/코딩/문제 선택(JCG-2126) */}
      {!hideTabControl && (
        <StudyProgressTabCard
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}

      {/* 이전 회차 보기 — 셸(데스크탑)에서 본문 인라인 대신 우측에서 회차 선택으로 통일(JCG-2126).
          현재 탭에 맞춰 모드/뷰를 바꾼다(overview/problems → stats, theory/coding → attendance). */}
      {hasAccess && user?.id && (
        <MyAside icon={History} title="이전 회차">
          {activeTab === "theory" || activeTab === "coding" ? (
            <PreviousSessionDialog
              mode="attendance"
              userId={user.id}
              trigger={
                <Button variant="outline" size="sm" className="w-full">
                  <History className="h-3.5 w-3.5 mr-1.5" />
                  이전 회차 출석 보기
                </Button>
              }
            />
          ) : (
            <PreviousSessionDialog
              mode="stats"
              statsView={activeTab === "problems" ? "problems" : "overview"}
              trigger={
                <Button variant="outline" size="sm" className="w-full">
                  <History className="h-3.5 w-3.5 mr-1.5" />
                  이전 회차 보기
                </Button>
              }
            />
          )}
        </MyAside>
      )}

      {/* 연속학습일 위젯 — 현재 연속일 + 활성 회차 출석 일수 + 최근 7일 링(JCG-2126) */}
      {hasAccess && (
        <MyAside icon={Flame} title="연속 학습일">
          {streakLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : streak ? (
            <div className="space-y-3">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-extrabold text-amber-600 tabular-nums dark:text-amber-400">
                  {streak.streak}
                </span>
                <span className="text-sm text-muted-foreground">일 연속</span>
                {streak.attendedToday && (
                  <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:bg-amber-950 dark:text-amber-400">
                    오늘 출석
                  </span>
                )}
              </div>
              <MyAsideStat label="이번 회차 출석" value={`${streak.totalDays}일`} />
              <div className="flex justify-between gap-1">
                {streak.last7.map((d) => (
                  <div
                    key={d.date}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <div
                      className={cn(
                        "h-6 w-full rounded-md",
                        d.attended ? "bg-amber-500" : "bg-muted",
                        d.isToday && "ring-2 ring-amber-300 dark:ring-amber-700",
                      )}
                      title={`${d.date}${d.attended ? " 출석" : ""}`}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      {d.weekday}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </MyAside>
      )}

      {/* 안내/바로가기 (JCG-2073) */}
      <MyAside icon={BarChart3} title="공부 진행상황 안내">
        <MyAsideStat
          label="진행상황 기능"
          value={hasAccess ? "사용 가능" : "잠금"}
          accent={hasAccess}
        />
        {hasAccess ? (
          <>
            <p className="border-t border-border pt-2.5 text-xs">
              이론·코딩 페이지의 출석체크 버튼을 누르면 출석으로 인정돼요. 문제를
              풀수록 시도율과 정답률이 함께 채워져요.
            </p>
            <MyAsideCta href="/theory">이론 학습하러 가기</MyAsideCta>
          </>
        ) : (
          <>
            <p className="border-t border-border pt-2.5 text-xs">
              진행상황 기록은 Premium 이상 이용권에서 사용할 수 있어요.
              업그레이드하면 출석과 정답률이 자동으로 쌓여요.
            </p>
            <MyAsideCta href="/membership">이용권 업그레이드하기</MyAsideCta>
          </>
        )}
      </MyAside>
    </div>
  );
}
